import { ROUTES } from 'Constants';
import { store } from 'Models/Store';

/**
 * Where this user's own bookings live.
 *
 * Two answers, because /staff-bookings is a customer-facing page for an **InvoicedUser** and a
 * **FixedPriceUser**: the nav sends those two to the table rather than to the booking cards, since a
 * company account reads its travel as a list. Everyone else gets the cards.
 *
 * A function rather than a constant because it reads the signed-in user, and one function rather
 * than the ternary written out at each call site — the nav and anything sending a reader "back to
 * your bookings" have to agree, and they did not: the charter view's back button knew about staff
 * and customers but not about the two roles in between, so a company account reading one of its own
 * charters was dropped on a bookings page it never uses.
 */
export function bookingsListUrl(): string {
	return store.isInvoicedUser || store.isFixedPriceUser ? '/staff-bookings' : '/bookings';
}

/**
 * Where the **Back** button on a charter goes, for whoever is pressing it.
 *
 * Deliberately a fixed destination per role rather than browser history. The customer charter view
 * is reached from four places — the booking cards, the charters table, the transactions table and
 * the staff schedule's own sidebar — so "wherever you came from" is four different answers, and for
 * staff it is usually the one screen the charter is not on.
 *
 *  * **Staff** — the charter's sidebar over the staff schedule, which is where a charter is actually
 *    worked on.
 *  * **An invoiced or fixed-price customer** — their own Charters tab, not the Bookings one beside
 *    it: they were reading a charter, so that is the list they came from and the list they want.
 *  * **Everyone else** — the booking cards, where their charters sit among their bookings.
 */
export function charterBackUrl(charterId: string): string {
	if (store.isStaff) {
		return ROUTES.STAFF_SCHEDULE_CHARTER.url(charterId);
	}

	const list = bookingsListUrl();

	return list === '/staff-bookings' ? `${list}?tab=charters` : list;
}
